// Split out of src/components/Onboarding.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// Onboarding.jsx re-exports everything it exported before, so existing imports keep working.

import { CopyrightFooter, Icon, LittleBankersHeaderMark } from "../Common.jsx";
import { apiRequestPinRecovery, apiResetPin, getDeviceOwnerAdultId } from "../../lib/api.js";
import { adultForDevice, adultLabel, otherAdults } from "../../lib/adults.js";
import { t as ct } from "../../lib/content.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* -------------------------------- PIN pad -------------------------------- */


export function PinPadButton({ className, onPress, children, "aria-label": ariaLabel }) {
  return (
    <button
      type="button"
      className={className}
      aria-label={ariaLabel}
      style={{ WebkitTapHighlightColor: "transparent", touchAction: "manipulation", userSelect: "none" }}
      onPointerDown={(e) => { e.preventDefault(); onPress(); }}
    >
      {children}
    </button>
  );
}


// Forgotten-PIN inline form (review 2.3) — clones PasscodeRecoveryRequest's structure
// and uniform-success copy, posting to request-pin-recovery instead. Swapped in for the
// pad body itself rather than shown alongside it, same one-thing-at-a-time shape as the
// Join tab's recovery panel.
function PinForgotForm({ code, onCancel }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  async function submit() {
    setErr("");
    if (!emailValid) { setErr("Enter the email address this household was set up with."); return; }
    setSending(true);
    try {
      await apiRequestPinRecovery(code, email.trim());
      setSent(true);
    } catch (e) {
      setErr(e.rateLimited ? e.message : "Couldn't send that right now — try again shortly.");
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    // The way back stays under the message (Alan, 7 Oct): this form sits inside the PIN modal,
    // whose own Cancel row is hidden while it shows, so without this button the only exit was
    // the Escape key, which a phone does not have.
    return (
      <div>
        <div className="border-t border-b border-slate-200 text-xs text-slate-600 py-2.5 mb-3">
          If that email matches this household's owner, a reset link is on its way — check your inbox (and spam folder). The link expires in 30 minutes.
        </div>
        <button data-testid="pin-forgot-back" className="w-full text-xs font-semibold text-slate-500 py-1.5" onClick={onCancel}>{ct("onboarding.back_to_pin", "Back to PIN entry")}</button>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">{ct("onboarding.pinforgot.email_hint", "Enter your own email address, the one you use for Little Bankers, and we'll send a link to reset your Parent PIN. This doesn't affect your household passcode.")}</p>
      <input
        type="email"
        inputMode="email"
        className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-2"
        placeholder="Owner's email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
      />
      {err && <div className="text-rose-600 text-xs font-semibold mb-2">{err}</div>}
      <button disabled={sending} className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 mb-2 disabled:opacity-50" onClick={submit}>
        {sending ? "Sending…" : "Send reset link"}
      </button>
      <button className="w-full text-xs font-semibold text-slate-500 py-1.5" onClick={onCancel}>{ct("onboarding.back_to_pin", "Back to PIN entry")}</button>
    </div>
  );
}

// LB-340 (screens D1/D2/D3) — WHOSE PIN this pad is asking for.
//
// A device claimed by an adult (it was proven with their PIN) asks for that adult's PIN; a
// shared family device — one that joined by passcode or QR, so nothing recorded who was
// holding it — asks for the Owner's. Either way the other adults are one tap away through
// "Log in as {name}", which is the whole of the feature on this screen: the pad, the
// digits, the lockout message and the forgotten-PIN link are all exactly as they were.
//
// `state` is optional. Without it (the device-setup flow, which runs before a household is
// in hand) the pad behaves precisely as it did before this ticket, and the worker reads an
// absent adultId as the Owner.
export function PinPad({ onSubmit, onCancel, error, code, state }) {
  const [digits, setDigits] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [asAdultId, setAsAdultId] = useState(null);
  const [picking, setPicking] = useState(false);
  const onCancelRef = useRef(onCancel);
  useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);

  const deviceAdultId = code ? getDeviceOwnerAdultId(code) : null;
  const asking = adultForDevice(state, asAdultId || deviceAdultId);
  const others = asking ? otherAdults(state, asking.id) : [];

  // The adult rides with the digits so the caller never has to ask the pad who it meant.
  useEffect(() => { if (digits.length === 6) { onSubmit(digits, asking ? asking.id : null); setDigits(""); } }, [digits]);
  useEffect(() => {
    function handleKey(e) {
      if (showForgot) { if (e.key === "Escape") setShowForgot(false); return; }
      if (e.key >= "0" && e.key <= "9") setDigits((d) => (d.length < 6 ? d + e.key : d));
      else if (e.key === "Backspace") setDigits((d) => d.slice(0, -1));
      else if (e.key === "Escape") onCancelRef.current();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [showForgot]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-xs shadow-lg">
        <div className="font-bold text-slate-800 mb-1">{ct("onboarding.pinpad.heading", "Parent Mode")}</div>
        {showForgot ? (
          <PinForgotForm code={code} onCancel={() => setShowForgot(false)} />
        ) : (
          <>
            {/* Whose PIN the pad is asking for, but only where that is a real question —
                a household with one adult would be told something it has no way to get
                wrong (Alan, 20 Sep). The "Log in as" link below stays exactly where it was:
                this line says who, that one changes it. */}
            {asking && others.length > 0 && (
              <p className="text-xs font-semibold text-slate-700 mb-1" data-testid="pinpad-asking-as">
                {ct("onboarding.pinpad.asking_as", "Logging in as {name}", { name: adultLabel(asking) })}
              </p>
            )}
            <p className="text-xs text-slate-500 mb-4">{ct("onboarding.pinpad.instruction", "Enter the 6-digit parent PIN")}</p>
            <div className="flex justify-center gap-2 mb-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={`w-3.5 h-3.5 rounded-full border-2 ${i < digits.length ? "bg-slate-900 border-slate-900" : "border-slate-300"}`} />
              ))}
            </div>
            {error && <p className="text-rose-600 text-xs text-center mb-2 font-semibold">{error}</p>}
            <div className="grid grid-cols-3 gap-2" style={{ touchAction: "manipulation" }}>
              {PIN_DIGITS.map((n) => (
                <PinPadButton key={n} className="bg-slate-100 active:bg-slate-200 rounded-xl py-3 text-lg font-semibold" onPress={() => setDigits((d) => (d.length < 6 ? d + n : d))}>{n}</PinPadButton>
              ))}
              <PinPadButton className="rounded-xl py-3 text-sm font-semibold text-slate-500" onPress={onCancel}>{ct("onboarding.cancel_button", "Cancel")}</PinPadButton>
              <PinPadButton className="bg-slate-100 active:bg-slate-200 rounded-xl py-3 text-lg font-semibold" onPress={() => setDigits((d) => (d.length < 6 ? d + "0" : d))}>0</PinPadButton>
              <PinPadButton className="rounded-xl py-3 text-slate-500" aria-label="Backspace" onPress={() => setDigits((d) => d.slice(0, -1))}>⌫</PinPadButton>
            </div>
            {/* LB-340: one other adult gets a named link; more than one opens a short list,
                because "Log in as Sam" is a better thing to tap than "Log in as someone
                else" and a list of two is not a screen worth building. */}
            {others.length > 0 && (picking && others.length > 1 ? (
              <div className="mt-3 border-t border-slate-100 pt-2">
                {others.map((a) => (
                  <button key={a.id} type="button" data-testid="pinpad-switch-adult"
                    className="w-full text-center text-xs font-bold text-slate-900 py-1.5"
                    onClick={() => { setAsAdultId(a.id); setPicking(false); setDigits(""); }}>
                    {adultLabel(a)}
                  </button>
                ))}
              </div>
            ) : (
              <button type="button" data-testid="pinpad-switch-adult"
                className="w-full text-center text-xs font-bold text-slate-900 underline mt-3"
                onClick={() => { if (others.length === 1) { setAsAdultId(others[0].id); setDigits(""); } else setPicking(true); }}>
                {others.length === 1
                  ? ct("onboarding.pinpad.log_in_as", "Log in as {name}", { name: adultLabel(others[0]) })
                  : ct("onboarding.pinpad.log_in_as_other", "Log in as someone else")}
              </button>
            ))}
            {code && (
              <button type="button" className="w-full text-center text-xs font-bold text-slate-900 underline mt-3" onClick={() => setShowForgot(true)}>
                Forgotten your PIN?
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Full-screen destination for the emailed PIN-recovery link:
// littlebankers.uk/?recover-pin=CODE&token=... — routed here directly from main.jsx,
// bypassing the invite gate exactly like PasscodeReset's route does. Mirrors
// PasscodeReset structurally, but deliberately has NO "generate for me": a 6-digit PIN
// is short enough that a generator would just invite writing it on a sticky note, so it
// requires typing it twice instead, same as the wizard's own PIN step.
export function PinReset() {
  const params = new URLSearchParams(window.location.search);
  const code = (params.get("recover-pin") || "").toUpperCase();
  const token = params.get("token") || "";
  const codeValid = /^[A-Z0-9]{6}$/.test(code);

  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function submit() {
    setError("");
    if (!codeValid || !token) { setError("This recovery link looks incomplete — request a new one."); return; }
    if (!/^\d{6}$/.test(newPin)) { setError("PIN must be exactly 6 digits."); return; }
    if (newPin === "123456") { setError("Choose a PIN other than the starter 123456."); return; }
    if (newPin !== confirmPin) { setError("PINs don't match."); return; }
    setBusy(true);
    try {
      await apiResetPin(code, token, newPin);
      setDone(true);
    } catch (e) {
      setError(e.rateLimited ? e.message : (e.message || "Couldn't reset the PIN — the link may have expired."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative min-h-screen min-h-[100dvh] flex flex-col p-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)" }}>
      <div className="flex-1 flex items-center justify-center">
        <div className="bg-white rounded-lg border border-slate-200 p-6 w-full max-w-sm md:max-w-[560px]">
          <div className="mb-3 flex justify-center"><LittleBankersHeaderMark /></div>
          {!codeValid || !token ? (
            <div className="text-center">
              <div className="font-bold text-slate-800 mb-1">{ct("onboarding.pinreset.link_invalid_heading", "Recovery link incomplete")}</div>
              <p className="text-xs text-slate-500 mb-4">{ct("onboarding.pinreset.link_invalid_body", 'This link is missing its code or token. Go back to the app and use "Forgotten your PIN?" on the PIN screen to request a fresh one.')}</p>
              <a href="/" className="inline-block bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 px-5">{ct("onboarding.pinreset.back_button", "Back to Little Bankers")}</a>
            </div>
          ) : done ? (
            <div className="text-center">
              <div className="mb-2 flex justify-center"><Icon id="ic-celebrate" size={36} hue="pink" /></div>
              <div className="font-bold text-slate-800 mb-1">{ct("onboarding.pinreset.success_heading", "All set")}</div>
              <p className="text-xs text-slate-500 mb-4">{ct("onboarding.pinreset.success_body", "Your new Parent PIN is ready. Use it to unlock Parent Mode on household {code}.", { code })}</p>
              <a href="/" className="inline-block w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5">{ct("onboarding.pinreset.go_button", "Go to Little Bankers")}</a>
            </div>
          ) : (
            <div>
              <div className="font-bold text-slate-800 mb-1 text-center">{ct("onboarding.pinreset.form_heading", "Reset your Parent PIN")}</div>
              <p className="text-xs text-slate-500 mb-4 text-center">{ct("onboarding.pinreset.form_subheading", "Household {code}. Choose a new 6-digit PIN below.", { code })}</p>
              <input
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-2 font-mono text-center tracking-widest"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="New PIN"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
              />
              <input
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-4 font-mono text-center tracking-widest"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Confirm new PIN"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
              />
              {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2 mb-3">{error}</div>}
              <button disabled={busy} className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 disabled:opacity-50" onClick={submit}>
                {busy ? "Resetting…" : "Reset PIN"}
              </button>
            </div>
          )}
        </div>
      </div>
      <CopyrightFooter />
    </div>
  );
}


export const PIN_DIGITS = [1,2,3,4,5,6,7,8,9];
